import { Divider, Spinner } from '@chakra-ui/react';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import classNames from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import { useDebounce } from '@revelio/core';
import { GET_SCHOOL_INFO } from '@revelio/data-access';
import type { TreeData } from '@revelio/ui';
import { Input, Text, TreeSelection } from '@revelio/ui';

import { useToast } from '../../hooks/useToast';
import styles from '../company-search-tree/CompanySearchTree.module.css';
import { SelectedItems } from '../company-search-tree/SelectedItems';

export type SchoolItem = {
  label: string;
  name: string;
  primary_name: string;
  rsid: string;
};

export interface SchoolSearchTreeProps {
  selections: Record<string, SchoolItem>;
  setSelections: React.Dispatch<
    React.SetStateAction<Record<string, SchoolItem>>
  >;
  treeHeight?: number;
  inputGroupSize?: 'sm' | 'md' | 'lg';
  autoFocus?: boolean;
}

const MIN_SEARCH_LENGTH = 3;

export const SchoolSearchTree: React.FC<SchoolSearchTreeProps> = ({
  selections,
  setSelections,
  treeHeight = 220,
  inputGroupSize = 'sm',
  autoFocus,
}) => {
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebounce(inputValue, 200);
  const schoolDataByIdRef = useRef<Map<string, SchoolItem>>(new Map());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const isSearchActive = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;

  const toast = useToast();

  const [{ data, fetching, error }] = useQuery({
    query: GET_SCHOOL_INFO,
    variables: { name: debouncedSearch, page: 1 },
    pause: !isSearchActive,
  });

  useEffect(() => {
    if (error) {
      toast({
        title: 'Search error',
        description:
          'Something went wrong searching schools. Please try again later.',
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }, [error, toast]);

  useEffect(() => {
    if (!data?.schoolInfo) return;
    for (const item of data.schoolInfo) {
      if (!item?.rsid) continue;
      const name = item.name ?? item.rsid;
      schoolDataByIdRef.current.set(item.rsid, {
        rsid: item.rsid,
        name,
        label: name,
        primary_name: name,
      });
    }
  }, [data]);

  const treeData = useMemo((): TreeData[] => {
    if (!isSearchActive || !data?.schoolInfo) return [];
    return (data.schoolInfo ?? [])
      .filter(
        (item): item is NonNullable<typeof item> & { rsid: string } =>
          !!item?.rsid
      )
      .map((item) => ({
        id: item.rsid,
        name: item.name ?? item.rsid,
        depth: 0,
      }));
  }, [isSearchActive, data]);

  const selectedIds = useMemo(() => Object.keys(selections), [selections]);

  const handleSelectionChange = useCallback(
    (newIds: string[]) => {
      setSelections((prev) => {
        const updated: Record<string, SchoolItem> = {};
        for (const id of newIds) {
          if (prev[id]) {
            updated[id] = prev[id];
          } else {
            const school = schoolDataByIdRef.current.get(id);
            if (school) updated[id] = school;
          }
        }
        return updated;
      });
    },
    [setSelections]
  );

  const handleRemove = useCallback(
    (rsid: string) => {
      setSelections((prev) => {
        const updated = { ...prev };
        delete updated[rsid];
        return updated;
      });
    },
    [setSelections]
  );

  const trimmedLen = inputValue.trim().length;

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <div className={styles.searchWrapper}>
          <Input
            size={inputGroupSize}
            ref={inputRef}
            placeholder="Search..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.toLowerCase())}
            className={styles.searchInput}
            wrapperClassName={styles.searchField}
            data-testid="async-tree-search-input"
            endSlot={(() => {
              if (fetching) {
                return (
                  <Spinner
                    w={3.5}
                    h={3.5}
                    color="green.600"
                    data-testid="async-tree-search-input-loading"
                  />
                );
              }
              if (inputValue === '') {
                return (
                  <IconMagnifyingGlass
                    size={16}
                    className={styles.silverIcon}
                  />
                );
              }
              return (
                <IconCrossSmall
                  size={16}
                  className={classNames(styles.silverIcon, styles.clearIcon)}
                  onClick={() => setInputValue('')}
                />
              );
            })()}
          />
        </div>
        {Object.keys(selections).length > 0 && (
          <>
            <SelectedItems
              items={Object.values(selections).map((s) => ({
                id: s.rsid,
                name: s.name,
              }))}
              onRemove={handleRemove}
            />
            <Divider mb={2} />
          </>
        )}
        <div onWheel={(e) => e.stopPropagation()}>
          {isSearchActive ? (
            <>
              {!fetching && (data?.schoolInfo ?? []).length === 0 && (
                <Text fontSize="x-sm" className={styles.searchStatus}>
                  No results found
                </Text>
              )}
              <TreeSelection
                key="search"
                data={treeData}
                value={selectedIds}
                onChange={handleSelectionChange}
                height={treeHeight}
                skipDefaultSort
                isLoading={fetching}
              />
            </>
          ) : (
            <div style={{ height: treeHeight }}>
              <Text fontSize="x-sm" className={styles.searchStatus}>
                {trimmedLen > 0
                  ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
                  : 'Start typing to see results'}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
